import React, { useState, useEffect, useContext } from 'react';
import * as Sentry from '@sentry/nextjs';
import { Trans } from '@lingui/react';
import { Header, Body } from '@organisms/modals/base';
import { UserContext } from '@lib/contexts/UserProvider';
import {
  AdyenResultCodeKind,
  ItemKind,
  PayForItemPayload,
  PaymentResultCodeKind,
  PaymentWellnessProviderKind,
  useMyPaymentMethodsLazyQuery,
  usePayForItemMutation,
  usePayForWellnessItemMutation,
  usePaymentsStorePaymentDetailsMutation,
} from '@gql/generated';
import AdyenForm, {
  onAdditionalPaymentDetails,
  PaymentMethods,
} from './AdyenFormModal';
import Spinner from '@components/Spinner';
import { PaymentModal } from './Elements';
import { WellnessMethods } from './wellnessMethods';
import useLoginModal from '@lib/hooks/useLoginModal';
import { prependHostName } from '@lib/utils';
import '@adyen/adyen-web/styles/adyen.css';

declare type Props = {
  open?: boolean;
  slug: string;
  // when this is provided its passed to graphql mutation
  flexiblePriceAmount?: number;
  // this is for display
  amount: number;
  event_title: string;
  currentPage?: string;
  successPageURL: string;
  failPageURL: string;
  currency: string;
  itemKind: ItemKind;
  sourcePage: string;
  discountCode?: string;
  enableOnetimePayment?: boolean;
  enabledPaymentMethods?: PaymentMethods;
  children(
    onOpen: () => void,
    arg1: { isOpen: boolean; openModal(): void; closeModal(): void }
  ): React.ReactNode;
  onPaymentComplete?(arg0: PayForItemPayload): void;
  handlePaymentCompletion?(arg0: PayForItemPayload): void;
  onPaymentSubmit?(): void;
};

/*
  IMPLEMENTATION REASONS:
  to avoid seeing the css taking effect, we load assets first
  we defer mounting the hook element (#component-container) until all assets are loaded
  then we render the hook el and when its mounted we initialize the adyen dropin
*/

const DEFAULT_PAYMENT_METHODS: PaymentMethods = ['card', 'swish', 'wellness'];
const ItemPaymentModal = (props: Props) => {
  const {
    children,
    open = false,
    slug,
    amount,
    currency,
    itemKind = ItemKind['LiveEvent'],
    flexiblePriceAmount = null,
    discountCode = null,
    enableOnetimePayment = true,
    enabledPaymentMethods = DEFAULT_PAYMENT_METHODS,
    // this is just a callback for doing something after the showing the confirmation
    onPaymentComplete,
    // if this is present, we won't do anything
    handlePaymentCompletion,
    successPageURL,
    failPageURL,
    sourcePage,
    onPaymentSubmit,
  } = props;
  const [_, { refetch: reloadPaymentMethod }] = useMyPaymentMethodsLazyQuery();
  const [currentUser, { refetch: reloadUser }] = useContext(UserContext);
  const [paymentError, setPaymentError] = useState(false);
  const [isOpen, setIsOpen] = useState(open);
  const [paymentData, setPaymentData] = useState(null);
  const [login_modal, { openModal: openLoginModal }] = useLoginModal({
    title: <Trans id="login_modal.title_for_payment" />,
    description: <Trans id="login_modal.helper_for_payment" />,
    registerUrlParams: {
      to: props.currentPage,
    },
  });

  const [storePaymentDetails, { loading: storePaymentDetailsLoading }] =
    usePaymentsStorePaymentDetailsMutation();
  const [payForWellness] = usePayForWellnessItemMutation({});
  const [runPaymentRequest, { loading }] = usePayForItemMutation({
    onCompleted(data) {
      if (data?.payForItem?.errors?.length > 0) {
        setPaymentError(true);
      }
    },
    onError(error) {
      setPaymentError(true);
      Sentry.captureException(new Error(error.message), {
        contexts: {
          user: {
            id: currentUser?.id,
          },
        },
      });
    },
  });

  const onClose = () => {
    setIsOpen(false);
  };

  const onOpen = () => {
    if (currentUser?.id) {
      setIsOpen(true);
    } else {
      openLoginModal();
    }
  };

  useEffect(() => {
    setPaymentError(false);
    // we clean up payment data if there is any
    // to hide confirmation if opened again
    if (!isOpen && paymentData) {
      setPaymentData(null);
    }
  }, [isOpen]);

  const onComplete = (paymentData) => {
    onClose();
    // refresh user data
    reloadPaymentMethod();
    reloadUser();
    //
    if (typeof handlePaymentCompletion === 'function') {
      // everything will be handled by this function
      return handlePaymentCompletion(paymentData);
    }
    // this will show the confirmation message
    setPaymentData(paymentData);
    // notify parent about payment completion
    onPaymentComplete?.(paymentData);
  };

  const handlePaymentSubmit = async (variables, instance) => {
    onPaymentSubmit?.();
    setPaymentError(false);
    try {
      const resp = await runPaymentRequest({
        variables: {
          slug,
          itemKind,
          discountCode,
          ...(flexiblePriceAmount
            ? { amountCents: flexiblePriceAmount * 100 }
            : {}),
          redirectToSuccess: prependHostName(successPageURL),
          redirectToFailed: prependHostName(failPageURL),
          sourcePage,
          ...variables,
        },
        context: {
          headers: {
            currency,
          },
        },
      });
      const resultCode = resp?.data?.payForItem?.resultCode;
      if (
        resp?.data?.payForItem === null ||
        (resp?.data?.payForItem?.errors?.length > 0 &&
          ![
            PaymentResultCodeKind['Refused'],
            PaymentResultCodeKind['Authorised'],
          ].includes(resultCode) &&
          !resp.data?.payForItem?.resultAction)
      ) {
        setPaymentError(true);
        Sentry.captureEvent({
          message: 'Failed payment',
          contexts: {
            user: {
              id: currentUser?.id,
            },
            response: {
              ...resp?.data?.payForItem,
              errors: resp?.data?.payForItem?.errors[0],
            },
          },
        });
      } else {
        if (
          ['threeDS2', 'qrCode', 'redirect'].includes(
            resp.data?.payForItem?.resultAction?.type
          )
        ) {
          instance['_order_id'] = resp.data?.payForItem?.orderId;
          instance.handleAction(resp.data?.payForItem?.resultAction);
        } else if (resultCode === PaymentResultCodeKind['Refused']) {
          setPaymentError(true);
        } else if (
          [
            PaymentResultCodeKind['Authorised'],
            PaymentResultCodeKind['Pending'],
          ].includes(resultCode)
        ) {
          onComplete(resp.data?.payForItem);
        }
      }
      return null;
    } catch (ex) {
      console.log(ex);
    }
  };

  const handleAdditionalPaymentDetails: onAdditionalPaymentDetails = async (
    response,
    instance,
    actions,
    closeDetailsModal
  ) => {
    try {
      const resp = await storePaymentDetails({
        variables: {
          threedsResult: response?.data?.details?.threeDSResult,
          payload: response?.data?.details?.payload,
          paymentData: response?.data?.paymentData,
          orderId: instance['_order_id'],
        },
      });

      const resultCode = resp?.data?.paymentsStorePaymentDetails?.resultCode;
      if (resultCode === AdyenResultCodeKind['Refused']) {
        setPaymentError(true);
        instance.update({});
      } else if (
        [
          AdyenResultCodeKind['Authorised'],
          AdyenResultCodeKind['Pending'],
        ].includes(resultCode)
      ) {
        onComplete(resp.data?.paymentsStorePaymentDetails);
      } else if (AdyenResultCodeKind['Cancelled'] === resultCode) {
        instance.update({});
      } else if (resp?.data?.paymentsStorePaymentDetails?.errors?.length > 0) {
        instance.update({});
        setPaymentError(true);
        closeDetailsModal();
      }
    } catch (ex) {
      instance.update({});
      setPaymentError(true);
      closeDetailsModal();
      Sentry.captureException(ex, {
        contexts: {
          user: {
            id: currentUser?.id,
          },
        },
      });
    }
  };

  let content = null;
  if (paymentData) {
    content = <Spinner size={18} />;
  } else {
    const _loading = loading || storePaymentDetailsLoading;
    content = (
      <Body pb={40}>
        <AdyenForm
          loading={_loading}
          amount={amount}
          currency={currency}
          onSubmit={handlePaymentSubmit}
          onAdditionalDetails={handleAdditionalPaymentDetails}
          paymentError={paymentError}
          enabledPaymentMethods={enabledPaymentMethods}
          onClose={onClose}
          wellnessForm={
            !_loading && enableOnetimePayment && currency === 'SEK' ? (
              <WellnessMethods
                getEpassiData={async () => {
                  const resp = await payForWellness({
                    variables: {
                      slug,
                      itemKind,
                      wellnessProvider: PaymentWellnessProviderKind['Epassi'],
                      discountCode,
                      amountCents: flexiblePriceAmount
                        ? flexiblePriceAmount * 100
                        : amount * 100,
                      redirectToSuccess: prependHostName(successPageURL),
                      redirectToFailed: prependHostName(failPageURL),
                      sourcePage,
                    },
                  });
                  return resp.data.payForWellnessItem;
                }}
              />
            ) : null
          }
        />
      </Body>
    );
  }

  const Content = isOpen ? (
    <PaymentModal
      open={isOpen}
      data-testid="ItemPaymentModal"
      // removed onclose to avoid user clicking out side by mistake while payment is on-going...
      // handleClose={onClose}
    >
      <Header handleClose={onClose} />
      {content}
    </PaymentModal>
  ) : null;

  return (
    <React.Fragment>
      {Content}
      {children(onOpen, { isOpen, openModal: onOpen, closeModal: onClose })}
      {login_modal}
    </React.Fragment>
  );
};

export default ItemPaymentModal;
